import axios from 'axios';
import {
  CREATE_BUSINESS_MODULE_REQUEST,
  CREATE_BUSINESS_MODULE_SUCCESS,
  CREATE_BUSINESS_MODULE_FAILURE,
  GET_BUSINESS_MODULE_REQUEST,
  GET_BUSINESS_MODULE_SUCCESS,
  GET_BUSINESS_MODULE_FAILURE,
  UPDATE_BUSINESS_MODULE_REQUEST,
  UPDATE_BUSINESS_MODULE_SUCCESS,
  UPDATE_BUSINESS_MODULE_FAILURE,
  DELETE_BUSINESS_MODULE_REQUEST,
  DELETE_BUSINESS_MODULE_SUCCESS,
  DELETE_BUSINESS_MODULE_FAILURE,
  CLEAR_BUSINESS_MODULE_ERROR,
  CLEAR_BUSINESS_MODULE_SUCCESS,
  baseURL
} from '../actions/types';

const API_BASE_URL = `${baseURL}/api`; // Adjust if your API is different

// Helper function to get authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token'); // Assuming 'token' is stored here
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Create Business Module (POST)
export const createBusinessModule = (moduleData) => async (dispatch) => {
  dispatch({ type: CREATE_BUSINESS_MODULE_REQUEST });
  try {
    const response = await axios.post(
      `${API_BASE_URL}/appointmentBusinessModules/create`,
      moduleData, // Data in body
      { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
    );
    dispatch({
      type: CREATE_BUSINESS_MODULE_SUCCESS,
      payload: response.data,
    });
    return response.data; // Return data for component to use if needed
  } catch (error) {
    dispatch({
      type: CREATE_BUSINESS_MODULE_FAILURE,
      payload: error.response?.data?.message || error.message,
    });
    throw error; // Re-throw to allow component to catch and display error
  }
};

// Get Business Module(s) (POST as per your Postman screenshot)
// Assuming get-business-modules also expects a body for filtering/ID
export const getBusinessModule = (queryData) => async (dispatch) => {
  dispatch({ type: GET_BUSINESS_MODULE_REQUEST });
  try {
    // If it's truly a POST for fetching, use post. If it should be GET with params, adjust.
    const response = await axios.post(
      `${API_BASE_URL}/appointmentBusinessModules/getall`,
      queryData, // Data in body
      { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
    );
    if (response.data.success === 1) {
      dispatch({ type: 'GET_BUSINESS_MODULE_SUCCESS', payload: response.data.data });
    } else if (response.data.success === 0 && response.data.message === "No Business Modules records found") {
      dispatch({ type: 'GET_BUSINESS_MODULE_EMPTY', payload: [] });
    } else {
      dispatch({ type: 'GET_BUSINESS_MODULE_FAIL', payload: response.data.message });
    }
  } catch (error) {
    console.error("Failed to fetch business modules:", error);
    dispatch({ type: 'GET_BUSINESS_MODULE_FAIL', payload: error.message || 'Network error' });
  }  
};

// Update Business Module (PATCH)
export const updateBusinessModule = (moduleId, updateData) => async (dispatch) => {
  dispatch({ type: UPDATE_BUSINESS_MODULE_REQUEST });
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/appointmentBusinessModules/update`, // Assuming ID in URL for patch
      updateData, // Data in body
      { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
    );
    dispatch({
      type: UPDATE_BUSINESS_MODULE_SUCCESS,
      payload: response.data,
    });
    return response.data;
  } catch (error) {
    dispatch({
      type: UPDATE_BUSINESS_MODULE_FAILURE,
      payload: error.response?.data?.message || error.message,
    });
    throw error;
  }
};

// Delete Business Module (DELETE)
export const deleteBusinessModule = (moduleId) => async (dispatch) => {
  dispatch({ type: DELETE_BUSINESS_MODULE_REQUEST });
  try {
    const response = await axios.delete(
      `${API_BASE_URL}api/appointmentBusinessModules/delete`, // Assuming ID in URL for delete
      { headers: getAuthHeaders() }
    );
    dispatch({
      type: DELETE_BUSINESS_MODULE_SUCCESS,
      payload: moduleId, // Often, delete success just needs the ID that was deleted
    });
    return response.data;
  } catch (error) {
    dispatch({
      type: DELETE_BUSINESS_MODULE_FAILURE,
      payload: error.response?.data?.message || error.message,
    });
    throw error;
  }
};

// Clear error and success messages
export const clearBusinessModuleError = () => ({ type: CLEAR_BUSINESS_MODULE_ERROR });
export const clearBusinessModuleSuccess = () => ({ type: CLEAR_BUSINESS_MODULE_SUCCESS });


// Get Complete Account Status for User - UPDATED to use both business_module and business_addons tables
export const getAccountStatus = (hospitalID) => async (dispatch) => {
  dispatch({ type: 'GET_ACCOUNT_STATUS_REQUEST' });
  
  try {
    // Get data from both tables in parallel
    const [businessModulesResponse, businessAddonsResponse] = await Promise.all([
      // Get base plans from business_module table
      axios.post(
        `${API_BASE_URL}/appointmentBusinessModules/getall`,
        { hospitalID: parseInt(hospitalID) },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      ),
      // Get paid modules from business_addons table
      axios.post(
        `${API_BASE_URL}/appointmentBusinessModules/getall`, // Adjust endpoint as needed
        { hospitalID: parseInt(hospitalID) },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      )
    ]);

    const businessModules = businessModulesResponse.data.success === 1 ? businessModulesResponse.data.data || [] : [];
    const businessAddons = businessAddonsResponse.data.success === 1 ? businessAddonsResponse.data.data || [] : [];

    console.log('[Account Status] Raw data:', {
      businessModules: businessModules.length,
      businessAddons: businessAddons.length,
      businessModules,
      businessAddons
    });

    // Process the data to create comprehensive account status
    const accountStatus = processAccountStatus(businessModules, businessAddons, hospitalID);
    
    dispatch({
      type: 'GET_ACCOUNT_STATUS_SUCCESS',
      payload: accountStatus
    });
    
    return accountStatus;
    
  } catch (error) {
    console.error("Failed to fetch account status:", error);
    dispatch({
      type: 'GET_ACCOUNT_STATUS_FAILURE',
      payload: error.response?.data?.message || error.message || 'Network error'
    });
    throw error;
  }
};

// UPDATED: Helper function to process account status using both tables
const processAccountStatus = (businessModules, businessAddons, hospitalID) => {
  // Filter plans and addons for this hospital
  const hospitalPlans = businessModules.filter(plan =>
    parseInt(plan.hospitalID) === parseInt(hospitalID)
  );

  const hospitalAddons = businessAddons.filter(addon =>
    parseInt(addon.hospitalID) === parseInt(hospitalID)
  );

  console.log('[Account Status] Filtered data:', {
    hospitalPlans: hospitalPlans.length,
    hospitalAddons: hospitalAddons.length
  });

  // Get the last record from business_module table (highest ID)
  let lastPlanRecord = null;
  let overallStatus = 'inactive';

  if (hospitalPlans.length > 0) {
    // Sort all plans by ID in descending order to get the last record
    const sortedPlans = hospitalPlans.sort((a, b) => (b.id || 0) - (a.id || 0));
    lastPlanRecord = sortedPlans[0];
    
    // Check if the last plan record is active
    if (isPlanActive(lastPlanRecord)) {
      overallStatus = 'active';
    }
    
    console.log('[Account Status] Last plan record:', {
      id: lastPlanRecord.id,
      type: lastPlanRecord.businessType,
      isActive: isPlanActive(lastPlanRecord)
    });
  }

  // Get active addons from business_addons table (similar to PlanSelection component)
  const activeBusinessAddons = hospitalAddons.filter(addon => {
    // Check hospital ID match
    const hospitalMatch = addon.hospitalID === parseInt(hospitalID);
    
    // Check status
    const statusMatch = addon.status === 'active';
    
    // Check validity date
    let dateValid = true;
    if (addon.valid_date) {
      const validDate = parseDate(addon.valid_date);
      dateValid = validDate && validDate > new Date();
    }

    const isActive = hospitalMatch && statusMatch && dateValid;

    if (isActive) {
      console.log('[Account Status] Active addon:', {
        addon: addon.addon_name,
        valid_date: addon.valid_date,
        status: addon.status
      });
    }

    return isActive;
  });

  console.log('[Account Status] Active business addons:', activeBusinessAddons);

  // UPDATED: Combine modules from both sources
  const activeModules = [];
  
  // 1. Add modules from last plan record (business_module table)
  if (lastPlanRecord && lastPlanRecord.addonModules && isPlanActive(lastPlanRecord)) {
    const modules = lastPlanRecord.addonModules.split(',').filter(Boolean);
    modules.forEach(module => {
      activeModules.push({
        module: module.trim(),
        valid_From: lastPlanRecord.valid_From,
        valid_To: lastPlanRecord.valid_To,
        transactionId: lastPlanRecord.transactionId,
        amount: lastPlanRecord.amount,
        planId: lastPlanRecord.id,
        businessType: lastPlanRecord.businessType,
        source: 'business_module',
        moduleType: 'plan_module'
      });
    });
  }
  
  // 2. Add modules from business_addons table
  activeBusinessAddons.forEach(addon => {
    activeModules.push({
      module: addon.addon_name,
      valid_From: addon.start_date,
      valid_To: addon.valid_date,
      transactionId: addon.transactionId,
      amount: addon.amount,
      planId: addon.id,
      businessType: 'addon', // Mark as addon type
      source: 'business_addons',
      moduleType: 'paid_addon',
      addonData: addon // Keep full addon data for reference
    });
  });

  console.log('[Account Status] Combined active modules:', activeModules);

  // Get all modules from both tables for reference
  const allModules = getAllModules(hospitalPlans, hospitalAddons);

  // Calculate summary statistics
  const summary = {
    totalPlans: hospitalPlans.length,
    activePlans: hospitalPlans.filter(plan => isPlanActive(plan)).length,
    expiredPlans: hospitalPlans.filter(plan => !isPlanActive(plan)).length,
    totalAddons: hospitalAddons.length,
    activeAddons: activeBusinessAddons.length,
    activeModules: activeModules.length,
    totalAmountSpent: calculateTotalAmountSpent(hospitalPlans, hospitalAddons),
    lastRecordId: lastPlanRecord ? lastPlanRecord.id : null
  };

  // Enhanced module access information - from both tables
  const moduleAccess = {
    hasVideo: hasModuleAccess(activeModules, ['Video', 'video', '29']),
    hasBlog: hasModuleAccess(activeModules, ['Blog', 'blog', '30']),
    hasSelfAppointment: hasModuleAccess(activeModules, ['Self Appointment', 'self', '22']),
    // Add other modules as needed
    allActiveModules: activeModules.map(m => ({
      name: m.module,
      source: m.source,
      type: m.moduleType,
      validTo: m.valid_To
    })),
    lastRecordId: lastPlanRecord ? lastPlanRecord.id : null,
    lastRecordType: lastPlanRecord ? lastPlanRecord.businessType : null
  };

  console.log('[Account Status] Final module access:', moduleAccess);

  return {
    // Basic info
    hospitalID: parseInt(hospitalID),
    overallStatus,
    lastUpdated: new Date().toISOString(),

    // Last plan record
    currentPlan: lastPlanRecord,
    
    // All data organized
    allPlans: {
      subscription: hospitalPlans.filter(plan => plan.businessType === 'subscription'),
      commission: hospitalPlans.filter(plan => plan.businessType === 'commission'),
      active: {
        subscription: hospitalPlans.filter(plan => plan.businessType === 'subscription' && isPlanActive(plan)),
        commission: hospitalPlans.filter(plan => plan.businessType === 'commission' && isPlanActive(plan))
      },
      expired: {
        subscription: hospitalPlans.filter(plan => plan.businessType === 'subscription' && !isPlanActive(plan)),
        commission: hospitalPlans.filter(plan => plan.businessType === 'commission' && !isPlanActive(plan))
      }
    },

    // Addons data
    allAddons: {
      all: hospitalAddons,
      active: activeBusinessAddons,
      expired: hospitalAddons.filter(addon => {
        if (addon.valid_date) {
          const validDate = parseDate(addon.valid_date);
          return !validDate || validDate <= new Date();
        }
        return false;
      })
    },

    // Module information - combined from both tables
    activeModules: activeModules,
    allModules: allModules,

    // Summary statistics
    summary,

    // Validity information
    validity: {
      subscription: getValidityInfo(hospitalPlans.filter(plan => plan.businessType === 'subscription')),
      commission: getValidityInfo(hospitalPlans.filter(plan => plan.businessType === 'commission')),
      modules: getModuleValidityInfo(activeModules),
      addons: getAddonValidityInfo(activeBusinessAddons),
      lastRecord: lastPlanRecord ? {
        status: overallStatus,
        daysLeft: lastPlanRecord.valid_To ? calculateDaysLeft(lastPlanRecord.valid_To) : null,
        validFrom: lastPlanRecord.valid_From,
        validTo: lastPlanRecord.valid_To,
        planId: lastPlanRecord.id,
        businessType: lastPlanRecord.businessType
      } : null
    },

    // Payment information
    payment: {
      totalSpent: summary.totalAmountSpent,
      planPayments: getPlanPayments(hospitalPlans),
      addonPayments: getAddonPayments(hospitalAddons),
      recentTransactions: getRecentTransactions(hospitalPlans, hospitalAddons)
    },

    // Enhanced module access information
    moduleAccess: moduleAccess,

    // Raw data references (for debugging)
    _rawData: {
      businessModules: hospitalPlans,
      businessAddons: hospitalAddons
    }
  };
};

// NEW: Calculate total amount spent from both tables
const calculateTotalAmountSpent = (plans, addons) => {
  const planAmount = plans.reduce((total, plan) => total + (parseFloat(plan.amount) || 0), 0);
  const addonAmount = addons.reduce((total, addon) => total + (parseFloat(addon.amount) || 0), 0);
  return planAmount + addonAmount;
};

// NEW: Get all unique modules from both tables
const getAllModules = (plans, addons) => {
  const allModules = new Set();
  
  // Modules from business_module table
  plans.forEach(plan => {
    if (plan.addonModules) {
      const modules = plan.addonModules.split(',').filter(Boolean);
      modules.forEach(module => allModules.add(module));
    }
  });
  
  // Modules from business_addons table
  addons.forEach(addon => {
    if (addon.addon_name) {
      allModules.add(addon.addon_name);
    }
  });
  
  return Array.from(allModules);
};

// NEW: Get addon validity information
const getAddonValidityInfo = (addons) => {
  return addons.map(addon => {
    const daysLeft = addon.valid_date ? calculateDaysLeft(addon.valid_date) : null;
    return {
      addon: addon.addon_name,
      status: daysLeft === null ? 'active' : (daysLeft > 0 ? 'active' : 'expired'),
      daysLeft,
      validFrom: addon.start_date,
      validTo: addon.valid_date,
      amount: addon.amount,
      transactionId: addon.transactionId
    };
  });
};

// NEW: Get plan payments
const getPlanPayments = (plans) => {
  return plans
    .filter(plan => plan.transactionId && plan.amount)
    .map(plan => ({
      transactionId: plan.transactionId,
      amount: plan.amount,
      date: plan.valid_From,
      type: plan.businessType,
      planId: plan.id,
      addonModules: plan.addonModules
    }));
};

// NEW: Get addon payments
const getAddonPayments = (addons) => {
  return addons
    .filter(addon => addon.transactionId && addon.amount)
    .map(addon => ({
      transactionId: addon.transactionId,
      amount: addon.amount,
      date: addon.start_date,
      type: 'addon',
      addonId: addon.id,
      addonName: addon.addon_name
    }));
};

// UPDATED: Get recent transactions from both tables
const getRecentTransactions = (plans, addons) => {
  const allTransactions = [
    ...plans
      .filter(plan => plan.transactionId && plan.amount)
      .map(plan => ({
        transactionId: plan.transactionId,
        amount: plan.amount,
        date: plan.valid_From,
        type: plan.businessType,
        id: plan.id,
        source: 'business_module',
        description: `${plan.businessType} plan`
      })),
    ...addons
      .filter(addon => addon.transactionId && addon.amount)
      .map(addon => ({
        transactionId: addon.transactionId,
        amount: addon.amount,
        date: addon.start_date,
        type: 'addon',
        id: addon.id,
        source: 'business_addons',
        description: `${addon.addon_name} addon`
      }))
  ];

  return allTransactions
    .sort((a, b) => {
      const aDate = parseDate(a.date);
      const bDate = parseDate(b.date);
      return bDate - aDate;
    })
    .slice(0, 10); // Last 10 transactions
};

// UPDATED: Helper function to check module access
const hasModuleAccess = (activeModules, moduleNames) => {
  return activeModules.some(module => {
    const moduleName = module.module.toLowerCase().trim();
    return moduleNames.some(name => name.toLowerCase().trim() === moduleName);
  });
};

// Keep all the existing helper functions (isPlanActive, parseDate, calculateDaysLeft, etc.)
const isPlanActive = (plan) => {
  if (plan.businessType === 'commission') {
    // Commission plans are always active unless they have an expiry date that's passed
    if (!plan.valid_To) return true;
    const validToDate = parseDate(plan.valid_To);
    return validToDate > new Date();
  } else {
    // Subscription plans must have a valid_To date in the future
    if (!plan.valid_To) return false;
    const validToDate = parseDate(plan.valid_To);
    return validToDate > new Date();
  }
};

const parseDate = (dateString) => {
  if (!dateString) return new Date(0);
  const parts = dateString.split('-');
  if (parts.length !== 3) return new Date(0);
  return new Date(parts[2], parts[1] - 1, parts[0]);
};

const calculateDaysLeft = (validTo) => {
  if (!validTo) return null;
  const parts = validTo.split('-');
  if (parts.length !== 3) return null;
  const validToDate = new Date(parts[2], parts[1] - 1, parts[0]);
  const diff = validToDate.getTime() - new Date().getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const getValidityInfo = (plans) => {
  if (plans.length === 0) return { status: 'no_active_plan', daysLeft: 0 };
  
  const sortedPlans = plans.sort((a, b) => {
    const aDate = parseDate(a.valid_To);
    const bDate = parseDate(b.valid_To);
    return bDate - aDate;
  });

  const latestPlan = sortedPlans[0];
  if (!latestPlan.valid_To) return { status: 'no_expiry', daysLeft: null };

  const validToDate = parseDate(latestPlan.valid_To);
  const today = new Date();
  const daysLeft = Math.ceil((validToDate - today) / (1000 * 60 * 60 * 24));

  return {
    status: daysLeft > 0 ? 'active' : 'expired',
    daysLeft: daysLeft > 0 ? daysLeft : 0,
    validFrom: latestPlan.valid_From,
    validTo: latestPlan.valid_To,
    planId: latestPlan.id
  };
};

const getModuleValidityInfo = (modules) => {
  return modules.map(module => {
    const daysLeft = module.valid_To ? calculateDaysLeft(module.valid_To) : null;
    return {
      module: module.module,
      status: daysLeft === null ? 'active' : (daysLeft > 0 ? 'active' : 'expired'),
      daysLeft,
      validFrom: module.valid_From,
      validTo: module.valid_To,
      source: module.source,
      type: module.moduleType
    };
  });
};

// Clear account status error
export const clearAccountStatusError = () => ({ 
  type: 'CLEAR_ACCOUNT_STATUS_ERROR' 
});

// UPDATED: Utility function to check specific module access
export const checkModuleAccess = (accountStatus, moduleName) => {
  if (!accountStatus || !accountStatus.moduleAccess) return false;
  
  const moduleNameLower = moduleName.toLowerCase().trim();
  
  switch (moduleNameLower) {
    case 'video':
    case 'video consultation':
      return accountStatus.moduleAccess.hasVideo;
    case 'blog':
    case 'blog management':
      return accountStatus.moduleAccess.hasBlog;
    case 'self appointment':
    case 'self':
      return accountStatus.moduleAccess.hasSelfAppointment;
    default:
      // Check if module exists in active modules from both tables
      return accountStatus.moduleAccess.allActiveModules.some(
        activeModule => activeModule.name.toLowerCase().trim() === moduleNameLower
      );
  }
};

// NEW: Helper function for empty account status
const getEmptyAccountStatus = (hospitalID) => ({
  hospitalID: parseInt(hospitalID),
  overallStatus: 'inactive',
  lastUpdated: new Date().toISOString(),
  currentPlan: null,
  allPlans: {
    subscription: [],
    commission: [],
    active: { subscription: [], commission: [] },
    expired: { subscription: [], commission: [] }
  },
  allAddons: {
    all: [],
    active: [],
    expired: []
  },
  activeModules: [],
  allModules: [],
  summary: {
    totalPlans: 0,
    activePlans: 0,
    expiredPlans: 0,
    totalAddons: 0,
    activeAddons: 0,
    activeModules: 0,
    totalAmountSpent: 0,
    lastRecordId: null
  },
  validity: {
    subscription: { status: 'no_active_plan', daysLeft: 0 },
    commission: { status: 'no_active_plan', daysLeft: 0 },
    modules: [],
    addons: [],
    lastRecord: null
  },
  payment: {
    totalSpent: 0,
    planPayments: [],
    addonPayments: [],
    recentTransactions: []
  },
  moduleAccess: {
    hasVideo: false,
    hasBlog: false,
    hasSelfAppointment: false,
    allActiveModules: [],
    lastRecordId: null,
    lastRecordType: null
  }
});